import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { customerDetailsSchema } from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import type { Customer } from "~/shared/types/quote-details-types.ts";

type CustomerNavigationConfig = {
	baseRoute: string;
	customerRoute: string;
};

export const useCustomerSelection = ({
	baseRoute,
	customerRoute,
}: CustomerNavigationConfig) => {
	const { customerId } = useParams();
	const navigate = useNavigate();
	const [globalSelectedCustomer, setGlobalSelectedCustomer] =
		useAtom(selectedCustomerAtom);
	const { isInternal } = useDeploymentType();

	const [selectedCustomer, setSelectedCustomer] = useState<
		Customer | undefined
	>();

	const {
		data: customerDetails,
		isLoading: queryIsCustomerLoading,
		isError,
	} = useQuery({
		queryKey: ["customer", customerId],
		queryFn: async () => {
			if (!customerId) return null;
			const result = await getAndValidate(
				apiRoutes.CUSTOMER.replace(":customerId", customerId),
				customerDetailsSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled: !!customerId,
	});

	useEffect(() => {
		if (customerDetails) {
			setSelectedCustomer(customerDetails);
		} else if (!customerId) {
			setSelectedCustomer(undefined);
		}
	}, [customerDetails, customerId]);

	const isCustomerLoading =
		(!!customerId && !selectedCustomer && !isError) || queryIsCustomerLoading;

	const activeCustomer =
		(isInternal
			? selectedCustomer
			: (selectedCustomer ?? globalSelectedCustomer)) ?? undefined;

	useEffect(() => {
		const selectedCustomerId = globalSelectedCustomer?.id;
		if (
			selectedCustomerId != null &&
			customerId !== String(selectedCustomerId)
		) {
			navigate(
				customerRoute.replace(":customerId", selectedCustomerId.toString()),
			);
		}
	}, [globalSelectedCustomer, navigate, customerId, customerRoute]);

	const handleSelectCustomer = useCallback(
		(customer: Customer | null) => {
			setGlobalSelectedCustomer(customer);
			if (customer) {
				setSelectedCustomer(customer);
				navigate(customerRoute.replace(":customerId", String(customer.id)));
			} else {
				setSelectedCustomer(undefined);
				navigate(baseRoute);
			}
		},
		[navigate, baseRoute, customerRoute, setGlobalSelectedCustomer],
	);

	return {
		activeCustomer,
		selectedCustomer,
		isCustomerLoading,
		isCustomerError: isError,
		handleSelectCustomer,
		urlCustomerId: customerId,
	};
};
